"use client"

import { type ComponentProps, useContext } from "react"
import { AuthUIContext } from "../../../lib/auth-ui-provider"
import { fileToBase64, resizeAndCropImage } from "../../../lib/image-utils"
import { getLocalizedError } from "../../../lib/utils"
import { cn } from "@papier/ui/lib/utils"
import type { AuthLocalization } from "../../../localization/auth-localization"
import { Card } from "@papier/ui/components/ui/card"
import { SettingsImagePicker } from "@papier/ui/components/ui/settings-image-picker"
import { UserAvatar } from "../../user-avatar"
import type { SettingsCardClassNames } from "../shared/settings-card"

export interface UpdateAvatarCardProps extends ComponentProps<typeof Card> {
    className?: string
    classNames?: SettingsCardClassNames
    localization?: AuthLocalization
}

export function UpdateAvatarCard({
    className,
    classNames,
    localization: localizationProp,
    ...props
}: UpdateAvatarCardProps) {
    const {
        hooks: { useSession },
        mutators: { updateUser },
        localization: authLocalization,
        avatar
    } = useContext(AuthUIContext)
    const localization = { ...authLocalization, ...localizationProp }
    const { data: sessionData, isPending, refetch } = useSession()

    const handleAvatarChange = async (file: File) => {
        if (!sessionData || !avatar) return
        const resizedFile = await resizeAndCropImage(
            file,
            crypto.randomUUID(),
            avatar.size,
            avatar.extension
        )
        const image = avatar.upload
            ? await avatar.upload(resizedFile)
            : await fileToBase64(resizedFile)
        if (!image) throw new Error(localization.IMAGE_UPDATE_ERROR)
        await updateUser({ image })
        await refetch?.()
    }

    const handleDeleteAvatar = async () => {
        if (!sessionData) return
        if (sessionData.user.image && avatar?.delete) {
            await avatar.delete(sessionData.user.image)
        }
        await updateUser({ image: null })
        await refetch?.()
    }

    return (
        <Card className={cn("w-full text-start", className, classNames?.base)} {...props}>
            <SettingsImagePicker
                className="px-5 sm:px-6"
                title={localization.AVATAR || "Avatar"}
                description={localization.AVATAR_DESCRIPTION || ""}
                hasImage={!!sessionData?.user.image}
                isPending={isPending}
                disabled={!sessionData || !avatar}
                preview={
                    <UserAvatar
                        isPending={isPending}
                        className="size-16 text-xl"
                        classNames={classNames?.avatar}
                        user={sessionData?.user}
                        localization={localization}
                    />
                }
                labels={{
                    upload: localization.UPLOAD_IMAGE,
                    change: localization.CHANGE_IMAGE,
                    remove: localization.REMOVE_IMAGE,
                    instructions: localization.AVATAR_INSTRUCTIONS,
                    uploading: localization.UPLOADING_IMAGE,
                    removing: localization.REMOVING_IMAGE,
                    updated: localization.IMAGE_UPDATED,
                    removed: localization.IMAGE_REMOVED,
                    invalidFile: localization.INVALID_IMAGE,
                    error: localization.IMAGE_UPDATE_ERROR
                }}
                getErrorMessage={(error) => {
                    const message: unknown = getLocalizedError({ error, localization })
                    return typeof message === "string" ? message : ""
                }}
                onUpload={handleAvatarChange}
                onRemove={handleDeleteAvatar}
            />
        </Card>
    )
}
